import { IEquipment } from '@frontend/equipment/types';
import { CommonMessage } from '@frontend/lang';
import { Logger } from '@frontend/logger';
import { PackageType } from '@frontend/package/types';
import { IProduct } from '@frontend/product/types';
import { SettingKey } from '@frontend/setting/types';
import { AvailableSlotConstraint, Slot } from '@frontend/slot/types';
import { TransactionType } from '@frontend/transaction/types';
import type { ReactNode } from 'react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useWizardPackage } from '../../hooks/useWizardPackage';
import { useWizardSettings } from '../../hooks/useWizardSettings';
import { useWizardTransaction } from '../../hooks/useWizardTransaction';
import { fetchFirstAvailableSlot } from '../../utils/available-slot';
import { useScreen } from '../useScreen';
import { CreateAuditTransactionFunctionality, useCreateAuditTransaction } from './use-create-audit-transaction';

interface ViewProps {
    isSubmitting: boolean;
    error: ReactNode | null;
    resetError: () => void;
}

function resolveEntityId(value: unknown): string | undefined {
    if (typeof value === 'string' && value.length > 0) return value;
    if (value && typeof value === 'object' && 'id' in value && typeof (value as { id: unknown }).id === 'string') {
        return (value as { id: string }).id;
    }
    return undefined;
}

export const useCreateAudit = (): ViewProps => {
    const { store, navigationState, wizardData, onSubmit, onBack } = useScreen();
    const wizardSettings = useWizardSettings(store);
    const hasStartedRef = useRef(false);
    const fatalErrorLoggedRef = useRef(false);

    const [resolvedSlot, setResolvedSlot] = useState<Slot | null>(null);
    const [isResolvingSlot, setIsResolvingSlot] = useState(true);

    const wizardSlot = (wizardData.slot as Slot | null | undefined) ?? null;
    const wizardSlotId = wizardSlot?.id;
    const product = wizardData.product as IProduct | string | undefined;
    const selectedProduct = wizardData.selectedProduct as IProduct | string | undefined;
    const equipment = wizardData.equipment as IEquipment | string | undefined;
    const wizardConstraints = wizardData.constraints as AvailableSlotConstraint | undefined;

    const productId =
        resolveEntityId(product) ??
        resolveEntityId(selectedProduct) ??
        (typeof equipment === 'object' ? equipment.product_id : undefined) ??
        wizardConstraints?.product_id ??
        undefined;

    const equipmentId = resolveEntityId(equipment) ?? wizardConstraints?.equipment_id ?? undefined;

    const accountId =
        navigationState.iot?.account_id ??
        navigationState.user?.accountId ??
        store?.wizard?.account_id ??
        (typeof product === 'object' ? product.account_id : undefined) ??
        (typeof selectedProduct === 'object' ? selectedProduct.account_id : undefined) ??
        (typeof equipment === 'object' ? equipment.account_id : undefined);

    const spotId = navigationState.iot?.spot_id ?? wizardConstraints?.spot_id ?? wizardSlot?.spot_id ?? undefined;

    const functionalityId = store?.functionality?.id;
    const functionalityApplication = store?.functionality?.application;
    const functionalityFeature = store?.functionality?.feature;
    const functionality = useMemo<CreateAuditTransactionFunctionality | null>(() => {
        if (!functionalityId || !functionalityApplication || !functionalityFeature) return null;
        return {
            id: functionalityId,
            application: functionalityApplication,
            feature: functionalityFeature
        };
    }, [functionalityApplication, functionalityFeature, functionalityId]);

    // Prefer audit package workflow; fall back to drop-off (same as manage-spot audits).
    const transactionWorkflowId = wizardSettings.transactionWorkflowId;
    const packageWorkflowId = wizardSettings.get(SettingKey.AUDIT_PACKAGE_WORKFLOW_ID) ?? wizardSettings.dropOffPackageWorkflowId;

    const workflowSettings = useMemo(
        () => ({
            transactionWorkflowId: transactionWorkflowId ?? '',
            auditPackageWorkflowId: packageWorkflowId ?? ''
        }),
        [packageWorkflowId, transactionWorkflowId]
    );

    const transactionContext = useWizardTransaction({
        store,
        wizardId: store?.wizard.id,
        autoFetch: false,
        autoCreate: false
    });
    const packageContext = useWizardPackage({
        store,
        wizardId: store?.wizard.id,
        autoFetch: false,
        autoCreate: false
    });

    const { isSubmitting, error, setError, createTransactionWithPackage } = useCreateAuditTransaction({
        id: 'CREATE_AUDIT_TRANSACTION',
        functionality,
        workflowSettings,
        slot: resolvedSlot,
        onTransactionCreated: async (transaction, auditPackage) => {
            await transactionContext.localUpdateTransaction({
                id: transaction.id,
                state: transaction.state,
                workflow_id: transaction.workflow_id,
                type: transaction.type,
                user_id: transaction.user_id,
                spot_id: transaction.spot_id,
                module_id: transaction.module_id,
                slot_id: transaction.slot_id,
                iot_id: transaction.iot_id,
                application: transaction.application,
                feature: transaction.feature
            });
            await packageContext.localUpdatePackage(auditPackage);
        },
        onComplete: () => onSubmit()
    });

    const wizardSlotRef = useRef(wizardSlot);
    wizardSlotRef.current = wizardSlot;
    const wizardConstraintsRef = useRef(wizardConstraints);
    wizardConstraintsRef.current = wizardConstraints;

    useEffect(() => {
        let cancelled = false;

        const resolveSlot = async () => {
            const currentWizardSlot = wizardSlotRef.current;
            if (currentWizardSlot) {
                setResolvedSlot(currentWizardSlot);
                setIsResolvingSlot(false);
                return;
            }

            // Keep the spinner while kiosk/wizard context is still loading.
            if (!accountId || !spotId || !functionality) {
                setIsResolvingSlot(true);
                return;
            }

            const existing = wizardConstraintsRef.current;
            const slotConstraints: AvailableSlotConstraint = {
                ...existing,
                account_id: accountId ?? existing?.account_id,
                spot_id: spotId ?? existing?.spot_id,
                product_id: productId ?? existing?.product_id,
                equipment_id: equipmentId ?? existing?.equipment_id,
                transaction_type: TransactionType.AUDIT,
                package_type: PackageType.AUDIT,
                application: functionality.application,
                feature: functionality.feature,
                functionality_id: functionality.id
            };

            setIsResolvingSlot(true);
            try {
                const slot = await fetchFirstAvailableSlot(accountId, spotId, slotConstraints);
                if (!cancelled) setResolvedSlot(slot);
            } catch (cause) {
                Logger.error('Failed to resolve SLOT for create audit transaction', cause);
                if (!cancelled) setResolvedSlot(null);
            } finally {
                if (!cancelled) setIsResolvingSlot(false);
            }
        };

        void resolveSlot();

        return () => {
            cancelled = true;
        };
    }, [accountId, equipmentId, functionality, productId, spotId, wizardSlotId]);

    useEffect(() => {
        if (hasStartedRef.current || isSubmitting || isResolvingSlot) return;

        // Context still loading — wait without surfacing an error.
        if (!functionality || !accountId || !spotId) return;
        if (!store?.functionalitySettings) return;

        const fail = (reason: string, details?: Record<string, unknown>) => {
            if (fatalErrorLoggedRef.current) return;
            fatalErrorLoggedRef.current = true;
            Logger.error(`Create audit transaction aborted: ${reason}`, details);
            setError(CommonMessage.ERRORS.DEFAULT);
        };

        if (!transactionWorkflowId || !packageWorkflowId) {
            fail('missing workflow settings', {
                functionalityId: functionality.id,
                transactionWorkflowId,
                packageWorkflowId,
                hasAuditPackageWorkflow: !!wizardSettings.get(SettingKey.AUDIT_PACKAGE_WORKFLOW_ID),
                hasDropOffPackageWorkflow: !!wizardSettings.dropOffPackageWorkflowId
            });
            return;
        }

        if (!resolvedSlot) {
            fail('no available SLOT', { accountId, spotId, productId, equipmentId, functionalityId: functionality.id });
            return;
        }

        hasStartedRef.current = true;
        setError(null);
        void createTransactionWithPackage({
            accountId,
            productId: productId ?? undefined,
            equipmentId
        });
    }, [
        accountId,
        createTransactionWithPackage,
        equipmentId,
        functionality,
        isResolvingSlot,
        isSubmitting,
        packageWorkflowId,
        productId,
        resolvedSlot,
        setError,
        spotId,
        store?.functionalitySettings,
        transactionWorkflowId,
        wizardSettings
    ]);

    const resetError = useCallback(() => {
        hasStartedRef.current = false;
        fatalErrorLoggedRef.current = false;
        setError(null);
        onBack();
    }, [onBack, setError]);

    return {
        isSubmitting: isSubmitting || isResolvingSlot,
        error,
        resetError
    };
};
